import { create } from 'zustand'
import { apiClient, setupAuthInterceptors, storage } from '@/services/api/client'
import { usersApi } from '@/services/api/users'
import type { UpdateUserDto } from '@flight-journal/shared'

interface User {
  id: string
  email: string
  canac_id: string | null
  first_name: string
  last_name: string
  full_name: string
  role: string
  email_verified: boolean
  organization_id: string | null
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  init: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  setTokens: (accessToken: string, refreshToken: string) => void
  refreshAccessToken: () => Promise<void>
  updateProfile: (userId: string, data: UpdateUserDto) => Promise<void>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,

  init: async () => {
    try {
      console.log('Initializing auth...')
      const accessToken = storage.getItem('accessToken')
      const refreshToken = storage.getItem('refreshToken')
      const userStr = storage.getItem('user')

      console.log('Tokens loaded:', {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        hasUser: !!userStr,
      })

      if (accessToken && refreshToken && userStr) {
        const user = JSON.parse(userStr)
        set({
          accessToken,
          refreshToken,
          user,
          isAuthenticated: true,
          isLoading: false,
        })
        console.log('Auth initialized successfully')
      } else {
        console.log('No stored credentials found')
        set({ isLoading: false })
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error)
      set({ isLoading: false })
    }
  },

  login: async (email: string, password: string) => {
    try {
      console.log('Attempting login...')
      const response = await apiClient.post('/api/v1/auth/login', {
        email,
        password,
      })

      const { user, access_token, refresh_token } = response.data

      console.log('Login successful, saving tokens...')

      storage.setItem('accessToken', access_token)
      storage.setItem('refreshToken', refresh_token)
      storage.setItem('user', JSON.stringify(user))

      console.log('Tokens saved to localStorage')

      set({
        user,
        accessToken: access_token,
        refreshToken: refresh_token,
        isAuthenticated: true,
      })

      console.log('Login complete, user authenticated')
    } catch (error: any) {
      console.error('Login failed:', error)
      throw new Error(error.response?.data?.error || 'Login failed')
    }
  },

  logout: async () => {
    // Try to notify the server first (with token for audit log)
    try {
      await apiClient.post('/api/v1/auth/logout')
    } catch (error) {
      console.error('Logout request failed:', error)
    }

    // Clear tokens locally
    storage.removeItem('accessToken')
    storage.removeItem('refreshToken')
    storage.removeItem('user')

    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
    })
  },

  setTokens: (accessToken: string, refreshToken: string) => {
    storage.setItem('accessToken', accessToken)
    storage.setItem('refreshToken', refreshToken)

    set({ accessToken, refreshToken })
  },

  refreshAccessToken: async () => {
    const { refreshToken } = get()

    if (!refreshToken) {
      // Try to get it from localStorage as fallback
      const storedRefreshToken = storage.getItem('refreshToken')

      if (!storedRefreshToken) {
        throw new Error('No refresh token available')
      }

      // Update state with stored token
      set({ refreshToken: storedRefreshToken })
    }

    const tokenToUse = get().refreshToken

    const response = await apiClient.post('/api/v1/auth/refresh', {
      refresh_token: tokenToUse,
    })

    const { access_token, refresh_token } = response.data

    get().setTokens(access_token, refresh_token)
  },

  updateProfile: async (userId: string, data: UpdateUserDto) => {
    try {
      const updatedUser = await usersApi.update(userId, data)

      // Update both state and localStorage
      storage.setItem('user', JSON.stringify(updatedUser))
      set({ user: updatedUser })
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update profile')
    }
  },
}))

// Set up auth interceptors after store is created to break circular dependency
setupAuthInterceptors(useAuthStore)
